import type { FC, SyntheticEvent } from "react";
import { useEffect, useRef, useState } from "react";

import { useCurrentStateAndParams } from "@uirouter/react";
import isEqual from "lodash/isEqual";
import styled from "styled-components";

import { useTranslate } from "@similarweb/i18n-provider";
import { createNewRowForMultiFilter } from "@similarweb/pro-logic-components";
import type {
    DefaultInputFieldProps as PLCDefaultInputFieldProps,
    IDynamicFilterRule,
} from "@similarweb/pro-logic-components";
import { Popover, PopoverContent, PopoverTrigger, SimpleChipItemWrap } from "@similarweb/ui-components";

import { keywordGroupsQueryClient } from "app/services/keyword-groups";
import { allTrackers } from "app/services/track/track";
import { AdvancedFilterButton } from "pro-features/components/filtersPanel/src/filtersPanel";
import { FlexRow } from "pro-features/styled components/StyledFlex/src/StyledFlex";
import { proNavigator } from "single-spa/react-app/ProNavigator";

import {
    multiIncludeExcludeType,
    translationKeysMap,
    TYPE_KEYWORD,
    TYPE_KEYWORD_LIST,
    MAX_KEYWORD_LIST_ITEMS,
} from "./consts";
import { FilterComponent } from "./FilterComponent";
import type { DefaultInputFieldProps } from "./FilterInputField";
import { FilterInputField } from "./FilterInputField";
import { TextFieldComponent } from "./TextFieldComponent";
import { TopicAutocomplete } from "./TopicAutocomplete";
import { adaptRulesForUrl, getDropdownOptions, getFilterButtonText, getRules } from "./utils";

const paramKeyMap = {
    [multiIncludeExcludeType.KEYWORD]: "multiIncludeExcludeKeywords",
    [multiIncludeExcludeType.URL]: "multiIncludeExcludeUrl",
    [multiIncludeExcludeType.TITLE]: "multiIncludeExcludeTitle",
    [multiIncludeExcludeType.TOPIC]: "multiIncludeExcludeTopic",
} as const;

interface IMultiIncludeExcludeFilterContainerProps {
    filterType: (typeof multiIncludeExcludeType)[keyof typeof multiIncludeExcludeType]; // "keyword" | "url"
}
const ChipWrapper = styled(FlexRow)`
    margin: 0;
    padding: 0;
    flex-wrap: wrap;
    align-items: center;
    list-style-type: none;
`;

const trackAppliedRules = (rules: IDynamicFilterRule[] = []) => {
    const { keywordCount, listCount, ruleTypes } = rules.reduce(
        (acc, rule) => {
            acc.ruleTypes += `, ${rule.type}+${rule.match}`;
            acc.keywordCount += rule.items?.filter((item) => item.type === TYPE_KEYWORD).length;
            acc.listCount += rule.items?.filter((item) => item.type === TYPE_KEYWORD_LIST).length;
            return acc;
        },
        { keywordCount: 0, listCount: 0, ruleTypes: "" },
    );

    allTrackers.dispatchTrackEvent(
        "Drop Down",
        "click",
        `keyword filter applied/${keywordCount}And${listCount}/${rules.length} rules${ruleTypes}`,
    );
};

const resolvedTextFieldComponent = (
    filterType: (typeof multiIncludeExcludeType)[keyof typeof multiIncludeExcludeType],
): FC<PLCDefaultInputFieldProps> => {
    return function (props: PLCDefaultInputFieldProps) {
        const { translate } = useTranslate();
        switch (filterType) {
            case multiIncludeExcludeType.URL:
            case multiIncludeExcludeType.TITLE:
                return (
                    <FilterInputField
                        {...(props as DefaultInputFieldProps)}
                        filterType={filterType}
                        applyButtonText={translate("common.add")}
                    />
                );
            case multiIncludeExcludeType.TOPIC:
                return <TopicAutocomplete {...props} />;
            default:
                return <TextFieldComponent {...props} />;
        }
    };
};

export const MultiIncludeExcludeFilterContainer: FC<IMultiIncludeExcludeFilterContainerProps> = ({ filterType }) => {
    const { translate } = useTranslate();
    const selectedKeys = translationKeysMap[filterType];
    const [errorMessage, setErrorMessage] = useState<string>();
    const [rules, setRules] = useState<IDynamicFilterRule[]>([]);

    const { params } = useCurrentStateAndParams();
    const { multiIncludeExcludeKeywords, multiIncludeExcludeUrl, multiIncludeExcludeTitle, multiIncludeExcludeTopic } =
        params;
    const [isPopupOpen, setIsPopupOpen] = useState(false);
    const emptyRule = createNewRowForMultiFilter(getDropdownOptions(filterType));
    const tempRules = useRef<IDynamicFilterRule[] | null>(null);

    const emptyRulesString = JSON.stringify(emptyRule);

    useEffect(() => {
        const loadRules = async () => {
            const loadedRules = await getRules(
                filterType,
                multiIncludeExcludeKeywords,
                multiIncludeExcludeUrl,
                multiIncludeExcludeTitle,
                multiIncludeExcludeTopic,
                emptyRule,
            );
            setRules(loadedRules);
        };

        void loadRules();
    }, [
        filterType,
        multiIncludeExcludeKeywords,
        multiIncludeExcludeUrl,
        multiIncludeExcludeTitle,
        multiIncludeExcludeTopic,
        emptyRulesString,
    ]);

    const closePopup = () => {
        setIsPopupOpen(false);
    };

    const countTotalKeywords = async (rules: IDynamicFilterRule[] | null): Promise<number> => {
        if (!rules) {
            return 0;
        }

        let total = 0;
        if (!rules) {
            return 0;
        }
        for (const rule of rules) {
            for (const item of rule.items) {
                if (item.type === TYPE_KEYWORD) {
                    total += 1;
                } else if (item.type === TYPE_KEYWORD_LIST && item.id) {
                    const keywordList = await keywordGroupsQueryClient.getGroupById(item.id);
                    total += keywordList?.Keywords?.length || 0;
                }
            }
        }
        return total;
    };

    const updateTemporaryItems = async (newRules: IDynamicFilterRule[] | null) => {
        if (newRules) {
            const totalKeywords = await countTotalKeywords(newRules);

            if (totalKeywords > MAX_KEYWORD_LIST_ITEMS) {
                setErrorMessage(
                    translate("keyword.analysis.filters.keyword.list.exceeds.limit", {
                        limit: MAX_KEYWORD_LIST_ITEMS,
                    }),
                );
            } else {
                setErrorMessage("");
            }
        }
        tempRules.current = newRules;
    };

    const onApply = (updatedRules?: IDynamicFilterRule[]) => {
        if (!isEqual(updatedRules, rules)) {
            const rulesToSubmit = updatedRules?.filter((rule) => rule.items.length);
            trackAppliedRules(rulesToSubmit);
            if (rulesToSubmit) {
                const adaptedForUrl = adaptRulesForUrl(rulesToSubmit, filterType);
                const adaptedValue = JSON.stringify(adaptedForUrl);
                if (filterType in paramKeyMap) {
                    void proNavigator.updateParams({
                        [paramKeyMap[filterType]]: adaptedValue,
                    });
                }
            }
        }
        void updateTemporaryItems(null);
        closePopup();
    };

    const onCancel = () => {
        void updateTemporaryItems(null);
        closePopup();
    };

    const onResetClick = (e: SyntheticEvent) => {
        e.stopPropagation();
        closePopup();
        if (filterType in paramKeyMap) {
            void proNavigator.updateParams({
                [paramKeyMap[filterType]]: undefined,
            });
        }
        void updateTemporaryItems(null);
    };

    const handlePopoverOpenChange = (open: boolean) => {
        setIsPopupOpen(open);
        if (open) {
            allTrackers.dispatchTrackEvent("Drop Down", "click", "Open keyword filter");
        } else if (errorMessage) {
            setErrorMessage("");
            void updateTemporaryItems(null);
        } else if (tempRules.current) {
            onApply(tempRules.current);
        }
    };

    const onAddRule = () => {
        allTrackers.dispatchTrackEvent("Drop Down", "click", "Add rule");
    };

    return (
        <Popover
            placement="bottom-start"
            open={isPopupOpen}
            onOpenChange={handlePopoverOpenChange}
            isModal={true}
            initialFocus={-1}
        >
            <PopoverTrigger asChild>
                <div>
                    {rules?.[0]?.items?.length ? (
                        <ChipWrapper>
                            <SimpleChipItemWrap
                                text={getFilterButtonText(rules, filterType)}
                                onCloseItem={onResetClick}
                            />
                        </ChipWrapper>
                    ) : (
                        <AdvancedFilterButton>{translate(selectedKeys.headerTextKey)}</AdvancedFilterButton>
                    )}
                </div>
            </PopoverTrigger>
            <PopoverContent>
                <FilterComponent
                    initialRules={rules}
                    onApply={onApply}
                    onCancelClick={onCancel}
                    onAddRule={onAddRule}
                    onUpdateRules={updateTemporaryItems}
                    onDeleteRow={(_, newRules) => updateTemporaryItems(newRules)}
                    filterType={filterType}
                    TextFieldComponent={resolvedTextFieldComponent(filterType)}
                    errorMessage={errorMessage}
                />
            </PopoverContent>
        </Popover>
    );
};
